<script lang="ts">
  import { api } from "../../../framework/js/api.js";
  import { toast } from "../../../framework/js/toast.js";
  import { spinner } from "../../../framework/js/spinner.js";
  import { createEventDispatcher, onMount } from "svelte";
  import Router, { push } from "svelte-spa-router";

  import Buttons from "../Buttons.svelte";
  import UpgradePromptModal from "../../../framework/components/UpgradePromptModal.svelte";
  import { buildAnswersPayload } from "../../../framework/js/briefAnswers";
  const STORAGE_KEY_PREFIX = "briefGenerationInProgress_";
  const JOB_STORAGE_KEY_PREFIX = "briefGenerationJobUuid_";
  const INTERRUPTED_MAX_AGE_MS = 10 * 60 * 1000; // 10 min

  function setGenerationInProgress(uuid: string, jobUuid?: string) {
    const v = String(Date.now());
    try {
      sessionStorage.setItem(STORAGE_KEY_PREFIX + uuid, v);
      localStorage.setItem(STORAGE_KEY_PREFIX + uuid, v);
      if (jobUuid) {
        sessionStorage.setItem(JOB_STORAGE_KEY_PREFIX + uuid, jobUuid);
        localStorage.setItem(JOB_STORAGE_KEY_PREFIX + uuid, jobUuid);
      }
    } catch (_) {}
  }
  function clearGenerationInProgress(uuid: string) {
    try {
      sessionStorage.removeItem(STORAGE_KEY_PREFIX + uuid);
      localStorage.removeItem(STORAGE_KEY_PREFIX + uuid);
      sessionStorage.removeItem(JOB_STORAGE_KEY_PREFIX + uuid);
      localStorage.removeItem(JOB_STORAGE_KEY_PREFIX + uuid);
    } catch (_) {}
  }
  function wasGenerationInProgress(uuid: string): boolean {
    const raw =
      (typeof sessionStorage !== "undefined" ? sessionStorage.getItem(STORAGE_KEY_PREFIX + uuid) : null) ||
      (typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY_PREFIX + uuid) : null);
    const startedAt = raw ? parseInt(raw, 10) : 0;
    return !!(startedAt && Date.now() - startedAt < INTERRUPTED_MAX_AGE_MS);
  }

  export let localAnswers: any = {};
  export let briefSteps: any[] = [];
  export let briefData: { sections: any[] } = { sections: [] };
  let briefGenerated = false;
  let generationComplete = false;
  let progressStage = 0;
  let progressMessage = "Preparing your brief...";
  /** Show confirm screen instead of auto-starting (Resume Draft, or returned after navigating away) */
  let showInterrupted = false;
  let showUpgradeModal = false;
  /** Which flow triggered the upgrade modal so we can auto-retry */
  let pendingGenerationType: 'generate' | 'regenerate' | null = null;
  
  const progressStages = [
    "Give me a moment. Connecting what you've told me.",
    "An experienced researcher is reading your answers.",
    "Working out what actually matters.",
    "Writing the decision, not the topic.",
    "Choosing frameworks a junior would skip.",
    "Recommending how to look, not asking you to pick.",
    "Putting the thinking on the page."
  ];

  function getBriefUuid(): string {
    const path = window.location.hash;
    const segments = path.split("/");
    if (segments.length > 2 && segments[1] === "brief") return segments[2];
    return "";
  }

  const JOB_POLL_MS = 2500;
  const JOB_MAX_POLLS = 240;

  async function waitForAiJob(jobUuid: string): Promise<{ ok: boolean; error?: string }> {
    for (let i = 0; i < JOB_MAX_POLLS; i++) {
      const st = await api.getAiJobStatus(jobUuid) as any;
      if (!st?.success || !st.data) {
        await new Promise((r) => setTimeout(r, JOB_POLL_MS));
        continue;
      }
      const status = st.data.status;
      if (status === "completed") {
        return { ok: true };
      }
      if (status === "failed") {
        return { ok: false, error: st.data.error_message || "Generation failed" };
      }
      await new Promise((r) => setTimeout(r, JOB_POLL_MS));
    }
    return {
      ok: false,
      error: "Generation is taking longer than expected. Check your briefs list or try again.",
    };
  }

  function answersForGeneration(answers: Record<number, string>) {
    if (briefSteps.length > 0) {
      return buildAnswersPayload(briefSteps, answers);
    }
    return Object.values(answers);
  }

  async function generateBrief(answers: any) {
    const uuid = getBriefUuid();
    try {
      const answersArray = answersForGeneration(answers);
      const body = await api.enqueueBriefGenerationAsync(uuid, answersArray) as any;

      if (!body.success) {
        if (body.data?.code === 'no_credits') {
          pendingGenerationType = 'generate';
          showUpgradeModal = true;
          showInterrupted = true;
        } else if (body.data?.code === 'already_running') {
          // Already running — just navigate; BriefView will pick up and poll.
          setGenerationInProgress(uuid);
          try { localStorage.removeItem(`localBriefCurrentStep_${uuid}`); } catch (_) {}
          push(`/briefs/${uuid}`);
        } else {
          toast.showNotification(false, body.message || "Failed to generate brief");
          showInterrupted = true;
        }
        return;
      }

      const jobUuid = body.data?.job_uuid as string;
      if (!jobUuid) {
        toast.showNotification(false, "Invalid server response");
        showInterrupted = true;
        return;
      }

      setGenerationInProgress(uuid, jobUuid);
      try { localStorage.removeItem(`localBriefCurrentStep_${uuid}`); } catch (_) {}
      push(`/briefs/${uuid}`);
    } catch (error) {
      console.error("Error generating brief:", error);
      toast.showNotification(false, "Failed to generate brief");
      showInterrupted = true;
    }
  }

  async function saveBriefAnswerResponse(response: any) {
    const path = window.location.hash;
    const segments = path.split("/");
    let uuid = "";
    if (segments.length > 2 && segments[1] === "brief") {
      uuid = segments[2];
      
      // Clear the saved step since the brief is now complete
      localStorage.removeItem(`localBriefCurrentStep_${uuid}`);
    }
    try {
      // Pass the answers array so backend can ensure all answers are saved
      const answersArray = answersForGeneration(localAnswers);
      // This will complete the draft version and copy draft answers to current, then snapshot
      let body = await api.saveBriefResponse(uuid, response, null, answersArray);
      if (body && !body.success) {
        console.error("Failed to save brief response:", body.message);
        toast.showNotification(false, body.message || "Failed to save brief. Please try again.");
      }
    } catch (error) {
      console.error("Failed to update brief answer", error);
      toast.showNotification(false, "Failed to save brief. Please try again.");
    }
  }

  const dispatch = createEventDispatcher();

  function handleNavigation(e: CustomEvent<any>) {
    const dir = e.detail.dir;
    dispatch("navigate", { dir, briefData });
  }

  onMount(() => {
    const uuid = getBriefUuid();
    // Auto-start only when user just clicked Next from last question (flag set in Brief.svelte)
    try {
      const autoGenerate = typeof sessionStorage !== 'undefined' && sessionStorage.getItem(`briefAutoGenerate_${uuid}`) === '1';
      if (autoGenerate) {
        sessionStorage.removeItem(`briefAutoGenerate_${uuid}`);
        setGenerationInProgress(uuid);
        generateBrief(localAnswers);
        return;
      }
    } catch (_) {}
    // Returning (Resume Draft or came back after navigating away): show confirm, don't auto-start
    showInterrupted = true;
  });

  async function regenerate(currentAnswers: any) {
    try {
        briefGenerated = false;
      spinner.show('Generating Brief...');

      const path = window.location.hash;
      const segments = path.split("/");
      let uuid = "";
      if (segments.length > 2 && segments[1] === "brief") {
        uuid = segments[2];
      }

      briefData = { sections: [] };
      const answersArray = answersForGeneration(currentAnswers);
      const body = await api.enqueueBriefGenerationAsync(uuid, answersArray) as any;

      if (!body.success) {
        if (body.data?.code === 'no_credits') {
          spinner.hide();
          pendingGenerationType = 'regenerate';
          showUpgradeModal = true;
          return;
        }
        toast.showNotification(false, body.message || "Failed to generate brief");
        return;
      }

      const jobUuid = body.data?.job_uuid as string;
      if (!jobUuid) {
        toast.showNotification(false, "Invalid server response");
        return;
      }

      const done = await waitForAiJob(jobUuid);
      if (!done.ok) {
        toast.showNotification(false, done.error || "Failed to regenerate brief");
        return;
      }

      await api.updateUserCredit();
      briefGenerated = true;
      try {
        localStorage.removeItem(`localBriefCurrentStep_${uuid}`);
      } catch (_) {}
    } catch (error) {
      console.error("Error regenerating brief:", error);
      toast.showNotification(false, "Failed to regenerate brief");
    } finally {
      spinner.hide();
    }
  }

  function handleUpgraded() {
    showUpgradeModal = false;
    if (pendingGenerationType === 'generate') {
      generateBrief(localAnswers);
    } else if (pendingGenerationType === 'regenerate') {
      regenerate(localAnswers);
    }
    pendingGenerationType = null;
  }

  let navigate = () => {
    window.location.href = `/#/dashboard`;
  }
</script>

{#if generationComplete}
  <!-- Completion Screen -->
  <div class="completion-screen">
    <div class="completion-icon">
      <i class="check circle icon"></i>
    </div>
    <h2 class="completion-title">Your Brief is Ready!</h2>
    <p class="completion-message">
      Your research brief has been generated successfully.
      <br />
      Redirecting you to view it now...
    </p>
    <div class="completion-spinner">
      <div class="ui active inline loader small"></div>
    </div>
  </div>
{:else if showInterrupted}
  <!-- Edge case: landed on gen step without confirm (e.g. direct URL). Single action to start. -->
  <div class="interrupted-screen">
    <button type="button" class="ui primary button" on:click={() => generateBrief(localAnswers)}>
      Generate Brief
    </button>
  </div>
{:else if !briefGenerated}
  <!-- Progress Screen -->
  <div class="generation-progress">
    <div class="progress-icon-container">
      <div class="progress-icon">
        <i class="magic icon"></i>
      </div>
      <div class="progress-rings">
        <div class="ring ring-1"></div>
        <div class="ring ring-2"></div>
        <div class="ring ring-3"></div>
      </div>
    </div>
    
    <h2 class="progress-title">Give me a moment — connecting what you've told me...</h2>
    <p class="progress-message">An experienced researcher is reading through your answers and working out what actually matters.</p>
    
    <div class="progress-steps">
      {#each progressStages as stage, index}
        <div class="progress-step {index < progressStage ? 'completed' : ''} {index === progressStage ? 'active' : ''}">
          <div class="step-icon">
            {#if index < progressStage}
              <i class="check icon"></i>
            {:else if index === progressStage}
              <div class="ui active tiny inline loader"></div>
            {:else}
              <i class="circle outline icon"></i>
            {/if}
          </div>
          <span class="step-text">{stage}</span>
        </div>
      {/each}
    </div>
    
    <div class="progress-bar-container">
      <div class="progress-bar-fill" style="width: {((progressStage + 1) / progressStages.length) * 100}%"></div>
    </div>
    <div class="progress-percent">
      {Math.round(((progressStage + 1) / progressStages.length) * 100)}%
    </div>
  </div>
{/if}

<UpgradePromptModal bind:showModal={showUpgradeModal} on:upgraded={handleUpgraded} />

<style>
  /* Generation Progress Screen */
  .generation-progress {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 2rem;
    text-align: center;
    min-height: 500px;
  }

  .progress-icon-container {
    position: relative;
    width: 120px;
    height: 120px;
    margin-bottom: 2rem;
  }

  .progress-icon {
    position: relative;
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    box-shadow: 0 8px 24px rgba(5, 108, 242, 0.3);
  }

  .progress-icon i {
    font-size: 3rem;
    color: white;
  }

  .progress-rings {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .ring {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border: 2px solid var(--primary);
    border-radius: 50%;
    opacity: 0;
    animation: pulse-ring 3s infinite;
  }

  .ring-1 {
    width: 120px;
    height: 120px;
    animation-delay: 0s;
  }

  .ring-2 {
    width: 120px;
    height: 120px;
    animation-delay: 1s;
  }

  .ring-3 {
    width: 120px;
    height: 120px;
    animation-delay: 2s;
  }

  @keyframes pulse-ring {
    0% {
      width: 120px;
      height: 120px;
      opacity: 0.8;
    }
    100% {
      width: 200px;
      height: 200px;
      opacity: 0;
    }
  }

  .progress-title {
    font-size: 2rem;
    font-weight: 500;
    color: var(--grey);
    margin: 0 0 1rem 0;
    background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .progress-message {
    font-size: 1.125rem;
    color: var(--grey-secondary);
    margin-bottom: 2.5rem;
    font-weight: 400;
  }

  .progress-steps {
    width: 100%;
    max-width: 500px;
    margin-bottom: 2rem;
  }

  .progress-step {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 1rem;
    margin-bottom: 0.5rem;
    border-radius: 8px;
    transition: all 0.3s ease;
    background: var(--greyish);
  }

  .progress-step.completed {
    background: linear-gradient(135deg, rgba(69, 196, 150, 0.1) 0%, rgba(69, 196, 150, 0.05) 100%);
  }

  .progress-step.active {
    background: linear-gradient(135deg, rgba(5, 108, 242, 0.1) 0%, rgba(71, 144, 238, 0.1) 100%);
    border: 1px solid var(--primary);
  }

  .step-icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .step-icon i {
    font-size: 1rem;
  }

  .progress-step.completed .step-icon i {
    color: #45c496;
  }

  .progress-step.active .step-icon {
    color: var(--primary);
  }

  .step-text {
    font-size: 0.9375rem;
    color: var(--grey);
    text-align: left;
  }

  .progress-step.completed .step-text {
    color: var(--grey-secondary);
  }

  .progress-step.active .step-text {
    color: var(--grey);
    font-weight: 500;
  }

  .progress-bar-container {
    width: 100%;
    max-width: 400px;
    height: 8px;
    background: var(--greyish);
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 0.5rem;
  }

  .progress-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--primary) 0%, var(--secondary) 100%);
    transition: width 0.5s ease;
    border-radius: 4px;
  }

  .progress-percent {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--primary);
    margin-top: 0.5rem;
  }

  /* Completion Screen */
  .interrupted-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
    min-height: 500px;
  }

  .completion-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
    min-height: 500px;
  }

  .completion-icon {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    background: linear-gradient(135deg, #45c496 0%, #3ab085 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 2rem;
    box-shadow: 0 8px 24px rgba(69, 196, 150, 0.3);
    animation: scale-in 0.5s ease-out;
  }

  .completion-icon i {
    font-size: 4rem;
    color: white;
  }

  @keyframes scale-in {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  .completion-title {
    font-size: 2.25rem;
    font-weight: 500;
    color: var(--grey);
    margin: 0 0 1rem 0;
    background: linear-gradient(135deg, #45c496 0%, #3ab085 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .completion-message {
    font-size: 1.125rem;
    color: var(--grey-secondary);
    margin-bottom: 2rem;
    line-height: 1.6;
  }

  .completion-spinner {
    margin-top: 1rem;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .generation-progress,
    .completion-screen,
    .interrupted-screen {
      padding: 2rem 1.5rem;
      min-height: 400px;
    }

    .progress-icon-container,
    .completion-icon {
      width: 100px;
      height: 100px;
    }

    .progress-icon i,
    .completion-icon i {
      font-size: 2.5rem;
    }

    .progress-title,
    .completion-title {
      font-size: 1.75rem;
    }

    .progress-message,
    .completion-message {
      font-size: 1rem;
    }

    .progress-steps {
      max-width: 100%;
    }

    .step-text {
      font-size: 0.875rem;
    }
  }
</style>
